import { useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import { journalsQueries } from "@/entities/journal";
import { $journalApi } from "@/entities/journal/fetch/fetch-client";
import { NOTIFICATION_MESSAGES } from "@/shared/utils";

export const useSectionThreeController = (
  sectionThreeListId: string,
  sectionThreeId: string,
) => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: journalsQueries.getSection3({ sectionThreeListId }).queryKey,
    });
  };

  const rejectSignEntry = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-three/{sectionThreeListId}/items/{itemNoVersionId}/status/signerRejected",
  );

  const statusReturnEntry = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-three/{sectionThreeListId}/items/{itemNoVersionId}/status/inputterReturn",
  );

  const returnEntry = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-three/{sectionThreeListId}/items/{itemNoVersionId}/return",
  );

  const sendToSignItem = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-three/{sectionThreeListId}/items/{itemNoVersionId}/sendToSignItem",
  );

  const sendToSignItems = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-three/{sectionThreeListId}/items/sendToSignItems",
  );

  const handleReject = ({
    itemNoVersionId,
    comment,
    signerEmployeeId,
  }: {
    itemNoVersionId: string;
    comment: string;
    signerEmployeeId: string;
  }) => {
    return new Promise<void>((resolve) => {
      rejectSignEntry.mutate(
        {
          params: {
            path: { sectionThreeListId, itemNoVersionId },
          },
          body: {
            comment,
            signer: { signerEmployeeId: signerEmployeeId || "" },
          },
        },
        {
          onSuccess: () => {
            notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_REJECTED_SUCCESS }); // "Запись отклонена"
            invalidate();
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getSectionThreeItem({ itemNoVersionId }).queryKey,
            });
          },
          onError: () => {
            notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_REJECT_ERROR }); // "Ошибка при отклонении"
            invalidate();
          },
          onSettled: () => resolve(),
        },
      );
    });
  };

  const handleStatusReturn = ({
    itemNoVersionId,
  }: {
    itemNoVersionId: string;
  }) => {
    return new Promise<void>((resolve) => {
      statusReturnEntry.mutate(
        {
          params: {
            path: { sectionThreeListId, itemNoVersionId },
          },
        },
        {
          onSuccess: () => {
            notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_RECALLED_FROM_SIGNING_SUCCESS }); // "Запись отозвана с подписания"
            invalidate();
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getSectionThreeItem({ itemNoVersionId }).queryKey,
            });
          },
          onError: () => {
            notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_RECALL_ERROR }); // "Ошибка при отзыве"
            invalidate();
          },
          onSettled: () => resolve(),
        },
      );
    });
  };

  const handleReturn = ({ itemNoVersionId }: { itemNoVersionId: string }) => {
    return new Promise<void>((resolve) => {
      returnEntry.mutate(
        {
          params: {
            path: { sectionThreeListId, itemNoVersionId },
          },
          // @ts-expect-error body не совпадает с openapi типами мутации
          body: {},
        },
        {
          onSuccess: () => {
            notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_RETURNED_TO_NEW }); // "Запись возвращена в статус NEW"
            invalidate();
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getSectionThreeItem({ itemNoVersionId }).queryKey,
            });
          },
          onError: () => {
            notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_RETURN_ERROR }); // "Ошибка при возврате записи"
            invalidate();
          },
          onSettled: () => resolve(),
        },
      );
    });
  };

  const handleSendToSign = ({
    itemNoVersionId,
  }: {
    itemNoVersionId: string;
  }) => {
    return new Promise<void>((resolve) => {
      sendToSignItem.mutate(
        {
          params: {
            path: { sectionThreeListId, itemNoVersionId },
          },
        },
        {
          onSuccess: () => {
            notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_SENT_TO_SIGNING }); // "Отправлено на подписание"
            queryClient.invalidateQueries({
              queryKey:
                journalsQueries.getSectionThreeSignersList(sectionThreeId)
                  .queryKey,
            });
            invalidate();
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getSectionThreeItem({ itemNoVersionId }).queryKey,
            });
          },
          onError: () => {
            notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_SEND_TO_SIGNING_ERROR_ALT }); // "Не удалось отправить на подписание"
            invalidate();
          },
          onSettled: () => resolve(),
        },
      );
    });
  };

  const handleSendToSignBatch = (
    items: { itemNoVersionId: string; selected: boolean }[],
  ) => {
    return new Promise<void>((resolve) => {
      sendToSignItems.mutate(
        {
          params: { path: { sectionThreeListId } },
          body: {
            itemsIds: items,
          },
        },
        {
          onSuccess: () => {
            notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_BULK_SENT_SUCCESS }); // "Групповая отправка выполнена"
            queryClient.invalidateQueries({
              queryKey:
                journalsQueries.getSectionThreeSignersList(sectionThreeId)
                  .queryKey,
            });
            invalidate();
          },
          onError: () => {
            notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_BULK_SEND_ERROR }); // "Ошибка при групповой отправке"
            invalidate();
          },
          onSettled: () => resolve(),
        },
      );
    });
  };

  return {
    handleReject,
    handleStatusReturn, // отмена подписи
    handleReturn, // возврат в статус NEW
    handleSendToSign,
    handleSendToSignBatch,
  };
};
